import React from "react";
import Navbar from "./Navbar";
import KeyFeatures from "./KeyFeatures";
import WhyUs from "./WhyUs";
import ClientTestimonials from "./ClientTestimonials";
import AboutUs from "./AboutUs";
import OurMission from "./OurMission";
import Vision from "./Vision";
import ContactUs from "./ContactUs";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import { FaGooglePlay, FaApple, FaCar, FaClock, FaMapMarkerAlt } from "react-icons/fa";

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-gray-50">
      {/* Static Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50 shadow-lg">
        <Navbar />
      </div>

      {/* Main Hero Section */}
      <section
        id="home"
        className="relative bg-gradient-to-br from-purple-900 via-blue-800 to-cyan-600 flex flex-col lg:flex-row justify-between items-center px-6 py-20 mt-20 overflow-hidden"
      >
        {/* Animated Background Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

        {/* Left Side Text */}
        <div className="text-white w-full max-w-2xl mb-8 lg:mb-0 relative z-10">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-2xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-yellow-300 to-cyan-300 bg-clip-text text-transparent">
              Welcome to <span className="text-yellow-300">Nupura Self Drive Cars</span>
            </h1>

            <p className="text-xl mb-4 leading-relaxed">
              At <span className="font-semibold text-yellow-300">Nupura Self Drive Cars</span>, we offer a wide range of premium vehicles, from compact cars to luxurious SUVs, all at competitive rates.
            </p>

            <p className="text-lg mb-4 text-blue-100">
              Whether you need a vehicle for business trips, family vacations, or long-term rentals, we provide flexible options tailored to your needs.
            </p>

            <p className="text-lg mb-6 text-blue-100">
              Experience seamless online booking and enjoy smooth, safe, and reliable rides with us.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button 
                onClick={() => navigate('/booking')}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center"
              >
                <FaCar className="mr-2" />
                Book Your Ride Now
              </button>
              
              <button 
                onClick={() => navigate('/contact')}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* Right Side: Enhanced Feature Cards */}
        <div className="w-full max-w-xl flex flex-col space-y-6 relative z-10">
          {/* Card 1 */}
          <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl shadow-2xl border border-white/30 hover:scale-105 transform transition-all duration-300 group hover:bg-white/25">
            <div className="flex items-start space-x-4">
              <div className="bg-yellow-400 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <FaCar className="text-2xl text-gray-900" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-yellow-300 mb-2">
                  🚗 Wide Vehicle Selection
                </h3>
                <p className="text-white text-sm leading-relaxed">
                  Choose from a variety of premium vehicles including sedans, SUVs, and luxury cars, all meticulously maintained for your comfort and safety.
                </p>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl shadow-2xl border border-white/30 hover:scale-105 transform transition-all duration-300 group hover:bg-white/25">
            <div className="flex items-start space-x-4">
              <div className="bg-cyan-400 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <FaClock className="text-2xl text-gray-900" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-cyan-300 mb-2">
                  ⏱ Fast & Easy Booking
                </h3>
                <p className="text-white text-sm leading-relaxed">
                  Our streamlined online booking system lets you reserve your perfect vehicle in minutes. Quick, simple, and hassle-free.
                </p>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl shadow-2xl border border-white/30 hover:scale-105 transform transition-all duration-300 group hover:bg-white/25">
            <div className="flex items-start space-x-4">
              <div className="bg-green-400 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <FaMapMarkerAlt className="text-2xl text-gray-900" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-300 mb-2">
                  🌍 Nationwide Coverage
                </h3>
                <p className="text-white text-sm leading-relaxed">
                  With services available across multiple cities, Nupura Self Drive Cars offers comprehensive car rental solutions wherever your journey takes you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rest of the Sections with Enhanced Background */}
      <div className="bg-gradient-to-b from-gray-50 to-white">
        <hr className="border-t-2 border-gray-200" />

        {/* Sections with subtle background gradients */}
        {[
          { id: "features", component: <KeyFeatures />, bg: "bg-white" },
          { id: "whyus", component: <WhyUs />, bg: "bg-gray-50" },
          { id: "aboutus", component: <AboutUs />, bg: "bg-white" },
          { id: "ourmission", component: <OurMission />, bg: "bg-gray-50" },
          { id: "vision", component: <Vision />, bg: "bg-white" },
          { id: "clientsays", component: <ClientTestimonials />, bg: "bg-gradient-to-r from-purple-50 to-blue-50" },
          { id: "contactus", component: <ContactUs />, bg: "bg-gray-900" }
        ].map(({ id, component, bg }) => (
          <React.Fragment key={id}>
            <hr className="border-t-2 border-gray-200" />
            <section
              id={id}
              className={`transform transition-transform ${bg}`}
            >
              {component}
            </section>
          </React.Fragment>
        ))}

        <hr className="border-t-2 border-gray-200" />
        <Footer />
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;